import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    Alert, Box, Chip, CircularProgress, Table, TableBody, TableHead, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import { FraudParty, FraudPerson, getFraudPeople } from "../../services/fraudService";
import { getApiErrorMessage } from "../../lib/toast";
import { formatDateTime, fraudPctColor } from "../../utils/fraud";
import {
    StyledTableCell, StyledTableContainer, StyledTableRow,
} from "../../assets/styles/tableStyle";

const TYPES: { value: FraudParty; label: string }[] = [
    { value: "DRIVER", label: "Drivers" },
    { value: "AGENT", label: "Agents" },
    { value: "RIDER", label: "Riders" },
];

const COLUMNS = ["Name", "Flagged rides", "Average", "Highest", "Last flagged"];

/** Highest-risk drivers / agents / riders over the last 30 days, from the nightly rebuild. */
const FraudPeople: React.FC = () => {
    const [type, setType] = useState<FraudParty>("DRIVER");
    const people = useQuery({
        queryKey: ["fraud-people", type],
        queryFn: async () => (await getFraudPeople(type)).data.items,
        meta: { suppressErrorToast: true },
    });
    const label = TYPES.find((t) => t.value === type)!.label.toLowerCase();
    const rows: FraudPerson[] = people.data ?? [];

    return (
        <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", my: 1.5 }}>
                <ToggleButtonGroup
                    size="small" exclusive value={type}
                    onChange={(_e, v: FraudParty | null) => v && setType(v)}
                    aria-label="Kind of person"
                >
                    {TYPES.map((t) => <ToggleButton key={t.value} value={t.value}>{t.label}</ToggleButton>)}
                </ToggleButtonGroup>
                <Typography variant="body2" color="text.secondary">
                    Last 30 days, rebuilt nightly; cleared flags are left out.
                </Typography>
            </Box>

            {people.isError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {getApiErrorMessage(people.error, "Couldn't load people at risk")}
                </Alert>
            )}

            {people.isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}><CircularProgress /></Box>
            ) : (
                <StyledTableContainer>
                    <Table sx={{ minWidth: 700 }}>
                        <TableHead>
                            <StyledTableRow>
                                {COLUMNS.map((c) => <StyledTableCell key={c}>{c}</StyledTableCell>)}
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((p) => (
                                <StyledTableRow key={p.id} hover data-testid={`fraud-person-${p.partyKey}`}>
                                    <StyledTableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{p.partyKey}</Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>{p.flaggedRides} of {p.rides} rides flagged</StyledTableCell>
                                    <StyledTableCell>{p.avgPct}%</StyledTableCell>
                                    <StyledTableCell>
                                        <Chip size="small" label={`${p.maxPct}%`} color={fraudPctColor(p.maxPct)} sx={{ fontWeight: 700 }} />
                                    </StyledTableCell>
                                    <StyledTableCell>{formatDateTime(p.lastFlaggedAt)}</StyledTableCell>
                                </StyledTableRow>
                            ))}
                            {people.isSuccess && rows.length === 0 && (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={COLUMNS.length} align="center">
                                        <Typography color="textSecondary" sx={{ py: 3 }}>
                                            No {label} at risk in the last 30 days.
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            )}
        </Box>
    );
};

export default FraudPeople;
